/**
 * Hook para definir las columnas de la tabla de estadísticas por producto
 * Hook to define columns for product statistics table
 */

import { Product } from '@shared/components/ui/Product';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductStatistics } from '../models/ProductStatistics';

interface UseAgGridColumnsProps {
  selectedColumns?: string[];
}

export const useAgGridColumns = ({ selectedColumns = [] }: UseAgGridColumnsProps) => {
  const { t } = useTranslation('logisticsModule');

  // Definiciones completas de columnas para AG Grid
  // Complete column definitions for AG Grid
  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      // Columna: Producto - Product column
      {
        field: 'product',
        headerName: t('table.product'),
        sortable: true,
        cellRenderer: (params: any) => {
          const data = params.data as ProductStatistics;
          if (!data.product) return '-';

          return (
            <Product
              id={data.product.id}
              name={data.product.name}
              src={data.product.image}
              code={data.product.code}
              showImageModal={true}
              enableLineClamp={true}
              showTooltip={true}
            />
          );
        },
        minWidth: 250,
        maxWidth: 400, // Limitar ancho máximo para nombres largos - Limit max width for long names
      },
      // Columna: Enviados - Sent column
      {
        field: 'sent',
        headerName: t('table.sent'),
        sortable: true,
        cellRenderer: (params: any) => {
          const data = params.data as ProductStatistics;
          return <span className="text-title">{data.sent || 0}</span>;
        },
        minWidth: 120,
      },
      // Columna: En tránsito - In transit column
      {
        field: 'in_transit',
        headerName: t('table.in_transit'),
        sortable: true,
        cellRenderer: (params: any) => {
          const data = params.data as ProductStatistics;
          const inTransit = data.in_transit;

          if (!inTransit) return '-';

          // Si hay porcentaje, mostrar número con porcentaje
          // If there's a percentage, show number with percentage
          if (inTransit.percentage !== undefined) {
            return (
              <span className="text-title">
                {inTransit.count} ({inTransit.percentage.toFixed(2)}%)
              </span>
            );
          }

          // Si hay valor en formato moneda, mostrarlo
          // If there's a currency value, show it
          if (inTransit.value) {
            return <span className="text-title">{inTransit.value}</span>;
          }

          // Por defecto, solo el número
          // Default, just the number
          return <span className="text-title">{inTransit.count || 0}</span>;
        },
        minWidth: 150,
      },
      // Columna: Entregados - Delivered column
      {
        field: 'delivered',
        headerName: t('table.delivered'),
        sortable: true,
        cellRenderer: (params: any) => {
          const data = params.data as ProductStatistics;
          const delivered = data.delivered;

          if (!delivered) return '-';

          // Si hay porcentaje, mostrar número con porcentaje
          // If there's a percentage, show number with percentage
          if (delivered.percentage !== undefined) {
            return (
              <span className="text-title">
                {delivered.count} ({delivered.percentage.toFixed(2)}%)
              </span>
            );
          }

          // Si hay valor decimal, mostrarlo
          // If there's a decimal value, show it
          if (delivered.value !== undefined) {
            return <span className="text-title">{delivered.value.toFixed(2)}</span>;
          }

          // Por defecto, solo el número
          // Default, just the number
          return <span className="text-title">{delivered.count || 0}</span>;
        },
        minWidth: 150,
      },
      // Columna: Devueltos - Returned column
      {
        field: 'returned',
        headerName: t('table.returned'),
        sortable: true,
        cellRenderer: (params: any) => {
          const data = params.data as ProductStatistics;
          const returned = data.returned;

          if (!returned) return '-';

          // Si hay porcentaje, mostrar número con porcentaje
          // If there's a percentage, show number with percentage
          if (returned.percentage !== undefined) {
            return (
              <span className="text-title">
                {returned.count} ({returned.percentage.toFixed(2)}%)
              </span>
            );
          }

          // Por defecto, solo el número
          // Default, just the number
          return <span className="text-title">{returned.count || 0}</span>;
        },
        minWidth: 150,
      },
    ];

    // Filtrar columnas según selectedColumns si se proporciona
    // Filter columns according to selectedColumns if provided
    if (selectedColumns.length > 0) {
      return baseColumns.filter((col) => {
        const field = col.field || '';
        return selectedColumns.includes(field);
      });
    }

    return baseColumns;
  }, [t, selectedColumns]);

  return {
    allColumnDefs,
    filteredColumnDefs: allColumnDefs,
  };
};
